import React from 'react';
import { Link } from 'react-router-dom';
import { Hexagon, Loader2, Sparkles, Lock, Users, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { getCategoryColor } from '../../utils/helpers';
import { iconMap } from './helpers';

export const MyListingsTab = ({
  myListings, setHoldersFor, openPriceHistory, handleRevalue, openBuyback,
  revalueLoading, formatCurrency,
}) => {
  if (myListings.length === 0) {
    return (
      <div data-testid="mylistings-tab">
        <div className="luxury-card p-12 text-center">
          <Hexagon size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-white font-serif text-xl mb-2">No Digital Twins Yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Tokenize an asset to see it here.</p>
          <Link to="/opportunities"><Button className="bg-gold text-black hover:bg-gold/90">Tokenise an Asset</Button></Link>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="mylistings-tab">
      <div className="grid md:grid-cols-2 gap-4" data-testid="my-listings-grid">
        {myListings.map((m) => {
          const Icon = iconMap[m.category] || Hexagon;
          return (
            <div key={m.id} className="luxury-card overflow-hidden" data-testid={`mylisting-${m.id}`}>
              <div className="relative h-40 bg-[#0A0A0A]">
                {m.image_url ? (
                  <img src={m.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${m.image_url}` : m.image_url} alt={m.asset_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Icon size={36} className={`${getCategoryColor(m.category)} opacity-30`} /></div>
                )}
                <div className="absolute top-3 left-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider bg-black/70 backdrop-blur-sm rounded ${getCategoryColor(m.category)}`}><Icon size={10} />{m.category}</span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 text-[10px] uppercase rounded ${
                    m.status === 'listed' ? 'bg-emerald-500/20 text-emerald-400' :
                    m.status === 'sold_out' ? 'bg-gold/20 text-gold' :
                    'bg-[#627EEA]/20 text-[#627EEA]'
                  }`}>{m.status === 'sold_out' ? 'Sold Out' : m.status}</span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <h3 className="text-white font-serif">{m.asset_name}</h3>
                  <p className="text-gold text-lg font-semibold">{formatCurrency(m.total_value)}</p>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-muted-foreground">Sold {m.sold_percent.toFixed(1)}%</span>
                    <span className="text-muted-foreground">{m.fractions_sold.toLocaleString()} / {m.total_fractions.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-gold to-[#F4D03F] rounded-full" style={{ width: `${m.sold_percent}%` }} />
                  </div>
                </div>
                <div className="pt-2 border-t border-[#1A1A1A] space-y-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Holders</p>
                      <p className="text-white font-medium">{m.holder_count}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground uppercase">Per fraction</p>
                      <p className="text-gold font-medium">{formatCurrency(m.price_per_fraction)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 text-[11px]" onClick={() => setHoldersFor(m)} data-testid={`view-holders-${m.id}`}>
                      <Users size={11} className="mr-1" /> Holders
                    </Button>
                    <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-[11px]" onClick={() => openPriceHistory(m)} data-testid={`price-history-${m.id}`}>
                      <TrendingUp size={11} className="mr-1" /> Price chart
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#627EEA]/30 text-[#627EEA] hover:bg-[#627EEA]/10 text-[11px]" disabled={revalueLoading === m.id} onClick={() => handleRevalue(m.id)} data-testid={`revalue-${m.id}`}>
                      {revalueLoading === m.id ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Sparkles size={11} className="mr-1" />}
                      Revalue
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[11px]" onClick={() => openBuyback(m)} data-testid={`untokenise-${m.id}`}>
                      <Lock size={11} className="mr-1" /> Un-tokenise
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
